import { z } from "zod";
import { Form, Formik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import type { ReactNode, MouseEvent, FormEvent } from "react";
import React, { useEffect, useState } from "react";
import useAppDispatch from "~/hooks/use-app-dispatch.ts";
import useAppSelector from "~/hooks/use-app-selector.ts";
import {
	signIn,
	signInConfirm,
	checkEmailAddress,
	signUp,
	changeEmailAddress,
	syncGetStartedPreferences,
} from "~/features/user/store-slice.ts";
import TextInputField from "~/components/forms/text-input-field.tsx";
import OtpInput from "~/components/forms/otp-input.tsx";
import { selectUser } from "~/features/user/selectors.ts";
import { pushGtmData } from "~/features/analytics/index.ts";
import { useWebsiteUrl } from "~/context/website-url.tsx";
import type { NameValues } from "./name-form.tsx";
import NameForm from "./name-form.tsx";

const validationSchema = z.object({
	emailAddress: z.string().min(1, "Required"),
});

type LoginOrSignUpFormProps = {
	readonly onSuccess?: () => void;
	readonly children?: ReactNode;
	readonly showJustSignedUpMessage: boolean;
	readonly finalSubmitLabel: string;
	readonly previouslyCollectedName?: string;
};

function LoginOrSignUpForm({
	finalSubmitLabel,
	showJustSignedUpMessage,
	onSuccess,
	children,
	previouslyCollectedName,
}: LoginOrSignUpFormProps) {
	const dispatch = useAppDispatch();

	const user = useAppSelector(selectUser);
	const parsedNameValues = useAppSelector(
		(state) => state.user.parsedNameValues,
	);
	const isWorking =
		user.type === "checkingSignIn" ||
		user.type === "checkingEmailAddress" ||
		user.type === "signingUp" ||
		user.type === "verifyingOtp";

	const showAskForName =
		(user.type === "signingUp" && previouslyCollectedName === undefined) ||
		(user.type === "checkedEmailAddress" &&
			!user.isUsed &&
			previouslyCollectedName === undefined);

	const showAskForOtp =
		user.type === "waitingForOtp" || user.type === "verifyingOtp";

	const privacyUrl = useWebsiteUrl("/policies/privacy-policy");
	const termsUrl = useWebsiteUrl("/policies/terms-of-service");

	// GTM
	useEffect(() => {
		if (showAskForName) {
			dispatch(
				pushGtmData({
					event: "PageView",
					page_title: "App Name Request",
				}),
			);
		}
	}, [dispatch, showAskForName]);
	useEffect(() => {
		if (showAskForOtp) {
			dispatch(
				pushGtmData({
					event: "PageView",
					page_title: "App OTP Request",
				}),
			);
		}
	}, [dispatch, showAskForOtp]);

	// Check email address
	const [pendingEmailAddress, setPendingEmailAddress] = useState<string>();
	const onEmailAddressSubmit = async ({
		emailAddress,
	}: z.output<typeof validationSchema>) => {
		setPendingEmailAddress(emailAddress);
		const result = await dispatch(checkEmailAddress(emailAddress));

		// If email exists, trigger OTP flow immediately
		if (result.payload === true) {
			setOtpValue(""); // Reset OTP value when starting OTP flow
			await dispatch(signIn({ emailAddress }));
		}
		// If email doesn't exist and we have parsed name values (even if empty), auto-signup
		else if (
			result.payload === false &&
			previouslyCollectedName !== undefined &&
			parsedNameValues
		) {
			const signUpResult = await dispatch(
				signUp({
					emailAddress,
					firstName: parsedNameValues.firstName,
					middleName: parsedNameValues.middleName,
					lastName: parsedNameValues.lastName,
					showJustSignedUpMessage,
				}),
			);

			// Sync preferences after successful auto-signup
			if (signUpResult.payload) {
				await dispatch(syncGetStartedPreferences());
			}

			if (onSuccess) {
				onSuccess();
			}
			await dispatch(
				pushGtmData({
					event: "AppRegistration",
				}),
			);
		}
	};
	const onChangeEmailAddress = (e: MouseEvent) => {
		e.preventDefault();
		dispatch(changeEmailAddress());
		setOtpValue(""); // Reset OTP value when changing email
	};

	// Sign in
	const [otpValue, setOtpValue] = useState("");

	const onOtpSubmit = async (e: FormEvent<HTMLFormElement>) => {
		if (user.type !== "waitingForOtp") {
			throw new Error("Invalid state - not waiting for OTP");
		}
		e.preventDefault();

		const result = await dispatch(
			signInConfirm({
				emailAddress: user.emailAddress,
				session: user.session,
				code: otpValue,
			}),
		);

		// Sync preferences after successful sign-in confirmation
		if (result.payload) {
			await dispatch(syncGetStartedPreferences());
			setOtpValue(""); // Reset OTP value on successful sign-in
		}

		dispatch(
			pushGtmData({
				event: "AppLogin",
			}),
		);
		if (onSuccess && result.payload) {
			onSuccess();
		}
	};

	// Sign up
	const onSignUpSubmit = async (values: NameValues) => {
		if (pendingEmailAddress === undefined) {
			throw new Error("Invalid state - no pending email");
		}

		const signUpResult = await dispatch(
			signUp({
				emailAddress: pendingEmailAddress,
				firstName: values.firstName,
				middleName: values.middleName,
				lastName: values.lastName,
				showJustSignedUpMessage,
			}),
		);

		// Sync preferences after successful signup
		if (signUpResult.payload) {
			await dispatch(syncGetStartedPreferences());
		}

		if (onSuccess) {
			onSuccess();
		}
		await dispatch(
			pushGtmData({
				event: "AppRegistration",
			}),
		);
	};

	return (
		<>
			{!showAskForOtp && (
				<Formik
					initialValues={{
						emailAddress: "",
					}}
					validationSchema={toFormikValidationSchema(validationSchema)}
					onSubmit={onEmailAddressSubmit}
				>
					<Form
						className="default-modal__email-form email-form"
						style={{ position: "relative" }}
					>
						{isWorking && (
							<div className="loading-overlay">
								<div className="loading-overlay__main">
									<div className="loading-overlay__spinner"></div>
									<div className="loading-overlay__title">
										{user.type === "signingUp"
											? "Creating your account..."
											: "Processing..."}
									</div>
								</div>
								<div className="loading-overlay__subtitle">
									This may take a few seconds
								</div>
							</div>
						)}
						<div className="email-form__field">
							<TextInputField
								type="email"
								name="emailAddress"
								className="email-form__input input"
								placeholder="Enter your email..."
								disabled={
									isWorking ||
									!["anonymous", "guest", "enteringEmail"].includes(user.type)
								}
							/>
							{[
								"anonymous",
								"checkingEmailAddress",
								"guest",
								"enteringEmail",
							].includes(user.type) ? (
								<button
									type="submit"
									className="email-form__submit-btn button"
									disabled={isWorking}
									data-testid="email-submit-button"
								>
									{user.type === "checkingEmailAddress"
										? "Checking"
										: "Continue"}
								</button>
							) : (
								<button
									type="button"
									className="email-form__submit-btn button"
									disabled={isWorking}
									onClick={onChangeEmailAddress}
								>
									Change
								</button>
							)}
						</div>
					</Form>
				</Formik>
			)}

			{showAskForOtp && (
				<>
					<form
						className="default-modal__email-form email-form email-form--otp"
						onSubmit={onOtpSubmit}
					>
						<div className="email-form__field">
							<OtpInput
								value={otpValue}
								onChange={setOtpValue}
								disabled={user.type === "verifyingOtp"}
								placeholder="Enter verification code"
								className="email-form__otp-input"
							/>
							<button
								type="submit"
								className="email-form__submit-btn button"
								disabled={user.type === "verifyingOtp" || otpValue.length !== 8}
							>
								{user.type === "verifyingOtp" ? "Verifying" : "Verify"}
							</button>
						</div>
					</form>
					<div className="default-modal__text default-modal__privacy">
						<p>We&apos;ve sent a verification code to your email address.</p>
					</div>
				</>
			)}

			{user.type === "waitingForOtp" && user.error && (
				<div className="error-message">{user.error}</div>
			)}

			{user.type === "checkedEmailAddress" && user.error && (
				<div className="error-message">{user.error}</div>
			)}
			{showAskForName && (
				<NameForm
					onSubmit={onSignUpSubmit}
					isWorking={isWorking}
					submitLabel={finalSubmitLabel}
					prefillName={previouslyCollectedName}
				/>
			)}
			{children}
			<div className="default-modal__text default-modal__privacy">
				By continuing, you indicate that you have read and agree to our{" "}
				<a href={termsUrl} target="_blank" rel="noopener noreferrer">
					Terms of Use
				</a>{" "}
				&amp;{" "}
				<a href={privacyUrl} target="_blank" rel="noopener noreferrer">
					Privacy Policy
				</a>
				.
			</div>
		</>
	);
}

export default LoginOrSignUpForm;
